import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ArrowRight, CheckCircle2, Package, PackageCheck } from 'lucide-react';
import SectionLabel from '../ui-shree/SectionLabel';
import { fetchVesselUnits } from '../../lib/api';

/**
 * GuardianLeaderboard — Home-page progress tracker to the first 1,000.
 *
 * A compact, quiet-luxury panel that pulls /api/guardians/count and
 * renders a sovereign progress line with milestone markers. Distinct
 * from /loyalty's full GuardianMilestone (which also handles referrals).
 *
 * The leaderboard is the count itself — every Guardian is a row in the
 * ledger, but we don't expose individual names on the public surface
 * (this is a witness, not a vanity board). What we publish:
 *   - the live count out of 1,000
 *   - the percentage written
 *   - the seats remaining
 *   - five gold milestone pins (100, 250, 500, 750, 1,000)
 *   - a single CTA to claim a seat (/loyalty#guardian-milestone)
 */

const MILESTONES = [100, 250, 500, 750, 1000];

const GuardianLeaderboard = ({ testId = 'home-guardian-leaderboard' }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchVesselUnits()
      .then((d) => alive && setData(d))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const reserved  = data?.vessels_reserved ?? 0;
  const shipped   = data?.vessels_shipped  ?? 0;
  const cap       = data?.founding_cap ?? 1000;
  const total     = reserved;        // legacy alias used by the milestone ruler
  const pct       = Math.min(100, Math.round((reserved / cap) * 1000) / 10);
  const remaining = Math.max(0, cap - reserved);

  return (
    <section
      id="guardian-leaderboard"
      data-testid={testId}
      className="relative px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9] border-t border-[#C1D7E2]"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-12">
          <div className="md:col-span-7">
            <SectionLabel testId={`${testId}-label`}>
              Founding Edition · physical units
            </SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#7C9CB1] font-light leading-[1.05] mb-8">
              The first <em className="text-[#90B0BF]">one thousand</em>
              <br />
              vessels are being made.
            </h2>

            {/* Side-by-side physical counters */}
            <div className="grid grid-cols-2 gap-5 md:gap-7 max-w-[640px]">
              {/* RESERVED */}
              <div
                data-testid={`${testId}-reserved-card`}
                className="border border-[#C1D7E2] bg-[#F4F8F9] p-5 md:p-6"
              >
                <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-[#90B0BF]">
                  <Package size={12} strokeWidth={1.6} />
                  Reserved
                </p>
                <p
                  data-testid={`${testId}-reserved-value`}
                  className="mt-3 font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-none"
                >
                  <span className="font-mono text-[#90B0BF]">{reserved.toLocaleString()}</span>
                  <span className="text-[#90B0BF]/80"> / {cap.toLocaleString()}</span>
                </p>
                <p className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#90B0BF]">
                  vessels held by Guardians
                </p>
              </div>

              {/* SHIPPED */}
              <div
                data-testid={`${testId}-shipped-card`}
                className="border border-[#D4AF5A] bg-[#D4AF5A]/[0.08] p-5 md:p-6 relative"
              >
                <span
                  aria-hidden
                  className="absolute top-0 left-5 right-5 h-px"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
                  }}
                />
                <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-[#7C9CB1] font-semibold">
                  <PackageCheck size={12} strokeWidth={1.6} className="text-[#D4AF5A]" />
                  Shipped
                </p>
                <p
                  data-testid={`${testId}-shipped-value`}
                  className="mt-3 font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-none"
                >
                  <span className="font-mono text-[#7C9CB1]">{shipped.toLocaleString()}</span>
                  <span className="text-[#90B0BF]/80"> / {cap.toLocaleString()}</span>
                </p>
                <p className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#90B0BF]">
                  vessels witnessed in hand
                </p>
              </div>
            </div>

            <p
              data-testid={`${testId}-pct`}
              className="mt-5 font-mono text-[10px] tracking-[0.28em] uppercase text-[#90B0BF]"
            >
              {pct.toFixed(1)}% of the founding edition · {remaining.toLocaleString()} vessels remain
            </p>
          </div>

          <aside className="md:col-span-5">
            <div className="relative border border-[#C1D7E2] bg-[#F4F8F9] p-8 md:p-10">
              <span
                aria-hidden
                className="absolute top-0 left-8 right-8 h-px"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
                }}
              />
              <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-5">
                <Users size={13} strokeWidth={1.5} /> The Founding Ledger
              </p>
              <p className="font-serif italic text-[#7C9CB1]/90 leading-relaxed">
                We do not publish names. The Circle is a witness, not a
                vanity board. Your number is your record — sequential,
                immutable, and yours alone.
              </p>
              <Link
                to="/loyalty#guardian-milestone"
                data-testid={`${testId}-cta`}
                className="group mt-6 inline-flex items-center gap-3 px-6 py-3 text-[10px] tracking-[0.28em] uppercase font-medium text-[#F4F8F9] bg-[#7C9CB1] hover:bg-[#90B0BF] transition-colors"
              >
                Claim a seat in the Circle
                <ArrowRight size={12} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </aside>
        </div>

        {/* Milestone ruler — 100, 250, 500, 750, 1000 */}
        <div data-testid={`${testId}-ruler`} className="relative mt-4">
          {/* Track */}
          <div
            className="relative h-[2px] bg-[#C1D7E2]"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              data-testid={`${testId}-fill`}
              className="absolute inset-y-0 left-0 transition-all duration-700"
              style={{
                width: `${Math.max(0.6, pct)}%`,
                background: 'linear-gradient(90deg, #D4AF5A, #7C9CB1)',
              }}
            />
          </div>

          {/* Milestone pins */}
          <ul className="mt-4 flex justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-[#90B0BF]">
            {MILESTONES.map((m) => {
              const hit = total >= m;
              const at  = (m / cap) * 100;
              return (
                <li
                  key={m}
                  data-testid={`${testId}-pin-${m}`}
                  className="flex flex-col items-center gap-1"
                  style={{ minWidth: '60px' }}
                >
                  <span
                    aria-hidden
                    className={`block w-2 h-2 rounded-full -mt-7 ${
                      hit ? 'bg-[#D4AF5A]' : 'bg-[#C1D7E2]'
                    }`}
                    style={{ boxShadow: hit ? '0 0 12px rgba(212,175,90,0.6)' : 'none' }}
                  />
                  <span className={hit ? 'text-[#7C9CB1] font-semibold' : ''}>
                    {m.toLocaleString()}
                  </span>
                  {hit && (
                    <CheckCircle2 size={10} strokeWidth={1.6} className="text-[#D4AF5A]" />
                  )}
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-[10px] tracking-[0.22em] uppercase font-mono text-[#90B0BF]/60 text-center">
            At {cap.toLocaleString()}, the gate quietly closes — the
            Guardian roll is sealed for the genesis era.
          </p>
        </div>
      </div>
    </section>
  );
};

export default GuardianLeaderboard;
